import {
  setSDKAuthHeader,
  deleteSDKAuthHeader,
  TPolicy,
  IRentersBreakUp,
  IAutoQuote,
  IRentersQuote,
  IUser,
  IAutoPolicy,
} from '@sureapp/platform-sdk';
import { isLogRocketEnabled } from '@sureapp/wl-toggle-common/dist/services/logRocket';
import { destructureAxiosReq } from '@sureapp/wl-toggle-common/dist/utils/destructureAxiosRequest';
import axios, { AxiosRequestConfig } from 'axios';
import set from 'lodash/set';
import LogRocket from 'logrocket';

import { URL_FROM_STORAGE } from '@@/auto/constants/api';
import {
  EPolicyUpdateReassignmentAction,
  TEligibilityCheckPayload,
  TGetLinksQuote,
} from '@@/auto/interfaces';
import { standardizeEmailForApi } from '@@/common/services/standardizeEmailForApi';
import { isAutoPolicy } from '@@/common/services/typeGuards';

const { REACT_APP_LN_COLLECTION_ID, REACT_APP_LN_CMS_CONTENT_ID } = process.env;
const persistedBaseUrl = URL_FROM_STORAGE && JSON.parse(URL_FROM_STORAGE);
const baseURL = persistedBaseUrl || process.env.REACT_APP_SURE_API_URL!;

const sureApi = axios.create({
  baseURL,
  headers: {
    'X-Space': 'farmers',
  },
});

if (isLogRocketEnabled()) {
  LogRocket.getSessionURL((sessionURL) => {
    sureApi.defaults.headers.common['x-logrocket-session-url'] = sessionURL;
  });
}

sureApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if ((error?.response?.headers ?? {})['x-request-id']) {
      set(
        error,
        'response.data.error._hrq',
        error.response.headers['x-request-id']
      );
    }
    return Promise.reject(error);
  }
);

function addUniqueKeyHeader(uid: string): AxiosRequestConfig {
  return {
    transformRequest: [
      (data, headers) => {
        if (headers) {
          // eslint-disable-next-line no-param-reassign
          headers['X-Unique-Key'] = uid;
        }
        return data;
      },
      ...[sureApi.defaults.transformRequest || []].flat(),
    ],
  };
}

const api = {
  updateBaseUrl(url: string) {
    sureApi.defaults.baseURL = url;
  },
  /**
   * Set the Authorization header on the axios instance.
   */
  setAuthHeader(accessToken: string) {
    setSDKAuthHeader(accessToken);
    sureApi.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  },

  /**
   * Delete the Authorization header on the axios instance.
   */
  deleteAuthHeader() {
    delete sureApi.defaults.headers.common.Authorization;
    deleteSDKAuthHeader();
  },

  /**
   * Create a quote.
   *
   * @param application
   */
  createQuote(application: unknown) {
    return destructureAxiosReq(
      sureApi.post('/api/quotes/v1/quotes', application)
    );
  },

  createLexisNextCms() {
    return destructureAxiosReq(
      sureApi.post(
        `/api/v2/collections/${REACT_APP_LN_COLLECTION_ID}/entries/${REACT_APP_LN_CMS_CONTENT_ID}/fields/`,
        []
      )
    );
  },

  updateLexisNexisCmsUsers(data: any) {
    return destructureAxiosReq(
      sureApi.patch(
        `/api/v2/collections/${REACT_APP_LN_COLLECTION_ID}/entries/${REACT_APP_LN_CMS_CONTENT_ID}/fields/`,
        data
      )
    );
  },

  /**
   * Get Quote
   * @param quoteNumber
   */
  getQuote<T extends IAutoQuote | IRentersQuote>(quoteNumber: string) {
    return destructureAxiosReq<T>(
      sureApi.get(`/api/quotes/v1/quotes/${quoteNumber}`)
    );
  },

  /**
   * Check region eligiblity for toggle auto
   * @param regionCode
   */
  getAutoEligibility(regionCode: string) {
    return destructureAxiosReq<TEligibilityCheckPayload>(
      sureApi.get(
        `/api/plans/v1/plans/verify-region-eligibility?region_code=${regionCode}&vertical_code=toggle_auto&carrier_code=farmers`
      )
    );
  },

  getAutoPartnerEligibility({
    regionCode,
    partnerCode,
  }: {
    regionCode: string;
    partnerCode?: string;
  }) {
    return destructureAxiosReq<TEligibilityCheckPayload>(
      sureApi.get(
        `/api/v2/plans/verify-region-partner-eligibility/?region_code=${regionCode}&vertical_code=toggle_auto&carrier_code=farmers${
          partnerCode ? `&partner_code=${partnerCode}` : ''
        }`
      )
    );
  },

  /**
   * Check region eligiblity for toggle renters
   * @param regionCode
   */
  getRentersEligibility(regionCode: string) {
    return destructureAxiosReq<TEligibilityCheckPayload>(
      sureApi.get(
        `/api/plans/v1/plans/verify-region-eligibility?region_code=${regionCode}&vertical_code=farmers_renters&carrier_code=farmers`
      )
    );
  },

  /**
   * Get available plans.
   */
  getPlans(quoteNumber: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/quotes/v1/quotes/${quoteNumber}/plans`)
    );
  },

  /**
   * Update a quote.
   */
  updateQuote(quoteNumber: string, quote: unknown) {
    return destructureAxiosReq(
      sureApi.put(`/api/quotes/v1/quotes/${quoteNumber}`, quote)
    );
  },

  /**
   * XXX: Setting up for Auto store
   * Get Vehicle Years
   * @param regionCode
   */
  getVehiclesYears(regionCode: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/vehicles/v1/years`, {
        params: {
          region_code: regionCode,
        },
      })
    );
  },

  /**
   * XXX: Setting up for Auto store
   * Get Vehicle Makes
   * @param year
   * @param regionCode
   */
  getVehiclesMakes(year: number, regionCode: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/vehicles/v1/makes`, {
        params: {
          year,
          region_code: regionCode,
        },
      })
    );
  },

  /**
   * XXX: Setting up for Auto store
   * Get Vehicle Makes
   * @param year
   * @param make
   * @param regionCode
   */
  getVehiclesModels(year: number, make: string, regionCode: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/vehicles/v1/models`, {
        params: {
          year,
          make,
          region_code: regionCode,
        },
      })
    );
  },

  /**
   *
   * Get occupation endpoints from the ROC (via Surepreme passthru)
   */
  getOccupations() {
    return destructureAxiosReq(sureApi.get(`/api/options/v1/occupations/`));
  },

  /**
   * Get quote variables.
   */
  getQuoteVars(quoteNumber?: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/quotes/v1/quotes/${quoteNumber}/variables`)
    );
  },

  /**
   * Checkout a quote.
   */
  checkoutQuote(quoteNumber?: string, application?: unknown) {
    if (application) {
      return destructureAxiosReq(
        sureApi.put(`/api/quotes/v1/quotes/${quoteNumber}/checkout`, {
          application,
        })
      );
    }

    return destructureAxiosReq(
      sureApi.get(`/api/quotes/v1/quotes/${quoteNumber}/checkout`)
    );
  },

  /**
   * Finalize a quote.
   */
  finalizeQuote<TQuote extends IRentersQuote | IAutoQuote>(
    quoteNumber?: string,
    opts = {},
    uid?: string
  ) {
    return destructureAxiosReq<TQuote>(
      sureApi.post(
        `/api/quotes/v1/quotes/${quoteNumber}/finalize`,
        opts,
        uid ? addUniqueKeyHeader(uid) : undefined
      )
    );
  },

  /**
   * Fetch a user's policy.
   */
  getPolicies() {
    return destructureAxiosReq(sureApi.get('/api/management/v1/policies', {}));
  },

  /**
   * Create a payment method.
   *
   * @param opts The options.
   * @param opts.token The source token.
   * @param opts.email The customer email.
   * @param opts.name The customer full name.
   */
  createPaymentMethod(
    opts: Partial<Record<'token' | 'email' | 'name', string>> = {}
  ) {
    return destructureAxiosReq(
      sureApi.post(`/api/management/v1/payment_methods`, {
        source_token: opts.token,
        email: opts.email,
        name: opts.name,
      })
    );
  },

  /**
   * Update a policy.
   *
   * @param policy The policy
   */
  updatePolicy(policy: TPolicy) {
    return destructureAxiosReq(
      sureApi.put(`/api/management/v1/policies/${policy.id}`, policy)
    );
  },

  getPolicy<Policy extends TPolicy = TPolicy>(policyId: string) {
    return destructureAxiosReq<Policy>(
      sureApi.get(`/api/management/v1/policies/${policyId}`)
    );
  },

  /**
   * Get policy variables.
   */
  getPolicyVars(policyId: string) {
    return destructureAxiosReq(
      sureApi.get(`/api/management/v1/policies/${policyId}/variables`)
    );
  },

  /**
   * Get policy checkout.
   */
  getPolicyCheckout(
    policy: TPolicy,
    options = { skip_fetch_additional_data: true }
  ) {
    const requestPayload = isAutoPolicy(policy)
      ? {
          ...policy,
          options,
        }
      : {
          variables: policy.details.variables,
          rating_address: policy.rating_address,
        };
    return destructureAxiosReq(
      sureApi.post(
        `/api/management/v1/policies/${policy.id}/checkout`,
        requestPayload
      )
    );
  },

  /*
   * Get policy change event list.
   */
  getPolicyChangeEvents(params: any) {
    return destructureAxiosReq(
      sureApi.get(`/api/v2/policies/policy-change-event-change?${params}`)
    );
  },

  /**
   * Get the user.
   */
  getUser() {
    return destructureAxiosReq(sureApi.get(`/api/user/v1/profile`));
  },

  /**
   * Update the user.
   */
  updateUser(user: IUser) {
    return destructureAxiosReq(sureApi.post(`/api/user/v1/profile`, user));
  },

  /**
   * Create LevelCredit user
   */
  createRenttrackUser() {
    return destructureAxiosReq(
      sureApi.post(`/api/user/v1/create-renttrack-user`, {})
    );
  },

  /**
   * Cancel a policy.
   */
  cancelPolicy(policyId: string, reason = '', comments = '') {
    return destructureAxiosReq(
      sureApi.delete(`/api/management/v1/policies/${policyId}`, {
        data: {
          reason,
          comments,
        },
      })
    );
  },

  /**
   * Get feature flags.
   */
  getFeatureFlags() {
    return destructureAxiosReq(sureApi.get(`/api/feature_flags/v1`));
  },

  /**
   * Submit request to prior carrier for termination
   * @param opts The options.
   */
  sendPriorCoverageTermination(
    opts: Partial<IRentersBreakUp & { cancellationDate: string }> = {}
  ) {
    return destructureAxiosReq(
      sureApi.post(`/api/user/v1/send-prior-termination-request`, {
        prior_insurance_company: opts.insuranceCompany,
        cancelled_date: opts.cancellationDate,
        prior_insurance_policy_number: opts.policyNumber,
        client_name: opts.policyHolderName,
        client_address: opts.address,
      })
    );
  },

  /**
   * Exchange OKTA activation token for state token
   */
  exchangeActivationToken(token: string | null) {
    return destructureAxiosReq(
      sureApi.post(`/api/auth/v1/okta-activation-token-exchange`, {
        token,
      })
    );
  },

  /**
   * This endpoint checks to see if a policy's rating address is covered by a moratorium
   */
  checkRatingAddressEligibility(policyId: string) {
    return destructureAxiosReq(
      sureApi.get(
        `/api/management/v1/policies/${policyId}/verify-address-eligibility`
      )
    );
  },

  /**
   * returns a list of billing schedule item events (charges, refunds, and failures
   * @param params
   */
  getBillingScheduleItems(params: string) {
    return destructureAxiosReq(
      sureApi.get(
        `/api/management/v1/policies/billing_schedule_item_events?${params}`
      )
    );
  },

  /**
   * Checks to see if email exists
   * @param email
   * @param carrier
   */
  checkEmailEligibility(email: string, carrier: string) {
    const emailToCheck = standardizeEmailForApi(email);
    return destructureAxiosReq(
      sureApi.get(
        `/api/user/v1/verify-email-availability?email=${emailToCheck}&carrier_code=${carrier}`
      )
    );
  },

  /**
   * Get cms content
   * @param slug - The cms collection slug
   */
  getCmsContent(slug: string) {
    return destructureAxiosReq(
      axios.get(`${process.env.REACT_APP_CMS_URL}/farmers/${slug}.json`)
    );
  },

  /**
   * Exchange renters quote auth token for tenant quote creation
   * @param quoteNumber - The quote number
   * @param token - The auth token to exchange
   */
  exchangeRentersQuoteAuthToken(quoteNumber: string, token: string) {
    return destructureAxiosReq<{ token: string }>(
      sureApi.post(process.env.REACT_APP_RENTERS_EXCHANGE_TOKEN_ENDPOINT!, {
        quote_number: quoteNumber,
        token,
      })
    );
  },

  /**
   * Exchange auto quote auth token for tenant quote creation
   * @param quoteNumber - The quote number
   * @param token - The auth token to exchange
   */
  exchangeAutoQuoteAuthToken(quoteNumber: string, token: string) {
    return destructureAxiosReq<{ token: string }>(
      sureApi.post(process.env.REACT_APP_AUTO_EXCHANGE_TOKEN_ENDPOINT!, {
        quote_number: quoteNumber,
        token,
      })
    );
  },

  /**
   * Get links for QA Auto purchase flow
   * @param quote - Tenant quote object
   */
  getLinkUrl(quote: TGetLinksQuote) {
    return destructureAxiosReq(sureApi.post(`/api/auth/v1/links`, quote));
  },

  /**
   * Checks if premium can be lowered
   * @param policyId - The policy id to check
   * @param isPartnerInitiated - If false, the credit reorder will not get
   *    marked as a partner reorder and will instead be logged as a
   *    user-initiated credit reorder.
   */
  reorderCredit<Policy extends TPolicy = TPolicy>(
    policyId: string,
    isPartnerInitiated = true
  ) {
    return destructureAxiosReq<Policy>(
      sureApi.post(`/api/management/v1/policies/${policyId}/credit-reorder`, {
        is_partner_credit_reorder: isPartnerInitiated,
      })
    );
  },

  /**
   * Trigger request to send a quote to the customer
   * @param quoteNumber - The quote number
   */
  sendQuoteAssistQuoteToCustomer(quoteNumber: string, returnPath: string) {
    return destructureAxiosReq(
      sureApi.post(`/api/quotes/v1/quotes/${quoteNumber}/email-to-user`, {
        relative_url: returnPath,
      })
    );
  },

  oktaPost({
    url,
    body,
    opts,
  }: {
    url: string;
    body: Record<string, any>;
    opts: { oktaIssuerUrl: string };
  }) {
    const { oktaIssuerUrl } = opts;
    const oktaBaseUrl = oktaIssuerUrl.split('/oauth2')[0];
    const finalUrl = url.startsWith('/') ? `${oktaBaseUrl}${url}` : url;
    return destructureAxiosReq(axios.post(finalUrl, body));
  },

  /**
   * Automatically updates Primary Driver assignments for a policy
   *
   */
  updatePrimaryDrivers({
    policy,
    policyId,
    reassignmentAction,
  }: {
    policyId: string;
    policy: IAutoPolicy;
    reassignmentAction?: EPolicyUpdateReassignmentAction;
  }) {
    return destructureAxiosReq<IAutoPolicy>(
      sureApi.post(`/api/v2/policies/${policyId}/update-primary-drivers/`, {
        ...policy,
        policy_update_reassignment_action: reassignmentAction,
      })
    );
  },

  sureApi,
};

export default api;
